import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { attemptChunkReload, isChunkLoadError } from '@/lib/chunkReload';

/**
 * `errorElement` del router — sostituisce la pagina di errore di default di React
 * Router («Unexpected Application Error!»), che e' quella che un utente vero ha visto
 * (collaudo sit&go su stg, 28/09) dopo un chunk vecchio.
 *
 * Due casi, mai confusi in una sola frase:
 *  - un import di chunk fallito (rilascio nuovo, asset vecchio sparito dall'origine):
 *    tenta lo STESSO ricarico guardato di `main.tsx` — copre il caso in cui l'errore
 *    arriva qui prima ancora che `vite:preloadError` scatti — e se la guardia ha gia'
 *    consumato il suo ricarico mostra la frase con l'azione «Ricarica»;
 *  - qualunque altro errore: una pagina leggibile, mai la frase del chunk.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation('general');
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (chunkError) attemptChunkReload(error);
  }, [error, chunkError]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h2 className="text-xl font-bold text-destructive">
        {chunkError ? t('chunkError.title') : t('chunkError.genericTitle')}
      </h2>
      <p className="max-w-sm text-sm text-hint">
        {chunkError ? t('chunkError.body') : t('chunkError.genericBody')}
      </p>
      <button
        type="button"
        className="mt-2 rounded-lg bg-button px-4 py-2 text-button-text"
        onClick={() => window.location.reload()}
      >
        {t('chunkError.reload')}
      </button>
    </div>
  );
}
